import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, downloadUrl } from "@/lib/api";
import { PACKAGES, gbp, entityLabel } from "@/lib/constants";
import { EmptyState } from "@/components/ui/bits";
import { Plus, Search, Download, Users, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data.filter(Boolean))).catch((e) => toast.error(apiError(e)));
  }, []);

  const filtered = clients.filter((c) => c.name?.toLowerCase().includes(q.toLowerCase()) || c.email?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3">
        <h1 className="font-serif font-bold text-3xl text-white">Clients</h1>
        <div className="flex gap-2">
          {user.role === "admin" && (
            <>
              <a href={downloadUrl("export/clients.csv")} className="card-surface border border-[#1E294B] rounded-full px-4 py-2.5 text-sm text-white flex items-center gap-2 hover:bg-[#18243F]" data-testid="export-clients">
                <Download size={16} /> <span className="hidden sm:inline">Export</span>
              </a>
              <button onClick={() => navigate("/clients/new")} data-testid="add-client"
                className="bg-[#C9A84A] hover:bg-[#E5C158] text-[#0A0F1F] font-bold rounded-full px-4 py-2.5 text-sm flex items-center gap-2">
                <Plus size={16} /> Add client
              </button>
            </>
          )}
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" data-testid="client-search"
          className="w-full card-surface rounded-xl pl-10 pr-4 py-3 text-white outline-none focus:border-[#C9A84A]/60" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" hint={user.role === "admin" ? "Onboard your first client to get started." : "No clients assigned to you yet."} />
      ) : (
        <div className="space-y-3">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => navigate(`/clients/${c.id}`)} data-testid={`client-card-${c.id}`}
              className="w-full card-surface rounded-2xl p-4 flex items-center justify-between hover:bg-[#18243F] transition-colors text-left">
              <div>
                <div className="text-white font-medium">{c.name}</div>
                <div className="text-slate-500 text-sm">{entityLabel(c.entity_type)} · {PACKAGES[c.package]?.name}</div>
                {c.assigned_staff_name && <div className="text-slate-500 text-xs mt-0.5">Managed by {c.assigned_staff_name}</div>}
              </div>
              <div className="flex items-center gap-3">
                <span className="gold-text font-serif font-bold">{gbp(c.fee)}/mo</span>
                <ChevronRight className="text-slate-600" size={18} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
